import { isBlank } from '@ember/utils';
import Model, { type AsyncBelongsTo, type SyncHasMany, attr, belongsTo, hasMany } from '@ember-data/model';
import type DeviceModel from 'garaje/models/device';
import type PrinterConnection from 'garaje/models/printer-connection';

export enum PrinterStatus {
  OPERATIONAL = 'operational',
  OFFLINE = 'printerOffline',
  NOT_IN_USE = 'notInUse',
  CHECK_IPADS = 'checkIpads',
}

// Given a `status` value for a printer, convert that into a PrinterStatus value.
// This is because `status` and `printerStatus` don't convey the same information.
type PrinterStatusMapping = {
  [key: string]: PrinterStatus;
};
const PRINTER_STATUS_MAP: PrinterStatusMapping = {
  connected: PrinterStatus.OPERATIONAL,
  'Not connected': PrinterStatus.OFFLINE,
};

// for `status` values that are not present in PRINTER_STATUS_MAP, use this as its `printerStatus`
const UNHANDLED_PRINTER_STATUS: PrinterStatus = PrinterStatus.OFFLINE;

// Printers of these types are always considered "operational" because there's no way to determine their true status.
const ALWAYS_OPERATIONAL_CONNECTION_TYPES = ['integration'];

// Printers of these types have their `printerStatus` determined by their connected iPads (or lack thereof)
// instead of using the `status` property on the model.
const IPAD_PRINTER_CONNECTION_TYPES = ['bluetooth', 'ip'];

export type PrinterConnectionType = 'ip' | 'bluetooth' | 'integration' | 'printnode';

export default abstract class AbstractPrinter extends Model {
  @hasMany('device', { as: 'abstract/printer', async: false, inverse: 'printer' })
  declare devices: SyncHasMany<DeviceModel>;

  // Reference to PrintNode "computer" record
  @belongsTo('printer-connection', { async: true, inverse: null })
  declare printerConnection: AsyncBelongsTo<PrinterConnection>;

  @attr('string')
  declare ip: string;

  @attr('boolean', { defaultValue: true })
  declare enabled: boolean;

  @attr('string')
  declare model: string;

  @attr('string')
  declare status: string;

  @attr('string', { defaultValue: 'ip' })
  declare connectionType: PrinterConnectionType;

  @attr('string')
  declare name: string;

  // Reference to PrintNode "printer" record
  @attr('string')
  declare externalResourceUid: string;

  /**
   * `devices` filtered down to iPads a person would recognize as such — excludes
   * ghost enrollment stubs and (unless the flag is on) simulators. See
   * `DeviceModel#isListableIpad` for why this must be grounded in devices-service
   * status telemetry rather than the raw sideloaded device record (SQ-7730).
   */
  get listableDevices(): DeviceModel[] {
    return (this.devices ?? []).filter((device) => device.isListableIpad);
  }

  get connectedOnlineDevices(): DeviceModel[] {
    const devices = this.listableDevices;
    return isBlank(devices)
      ? []
      : devices.filter(
          (device) =>
            device.livePrinterStatus &&
            device.isIpadSeenRecently &&
            device.livePrinterStatus.toLowerCase() === 'connected',
        );
  }

  get connectedDevices(): DeviceModel[] {
    const devices = this.listableDevices;
    return isBlank(devices)
      ? []
      : devices.filter((device) => device.livePrinterStatus && device.livePrinterStatus.toLowerCase() === 'connected');
  }

  /**
   * Determime the status of the printer. For certain printer types (see the `IPAD_PRINTER_CONNECTION_TYPES` array)
   * the status is determined from the printer's connected devices (or lack thereof) instead of using the printer's
   * `status` attribute.
   * Certain other printer types (see `ALWAYS_OPERATIONAL_CONNECTION_TYPES`) do not report an accurate status and do
   * not have connected devices; these printers are treated as always being 'operational'.
   *
   * Possible printer status
   * 1. operational     All iPads are paired with the printer
   * 2. notInUse        No iPads is paired with the printer
   * 3. printerOffline  All iPads reported printer is offline
   * 4. check iPads     Some iPads reported printer is connected
   */
  get printerStatus(): PrinterStatus {
    if (ALWAYS_OPERATIONAL_CONNECTION_TYPES.includes(this.connectionType)) {
      return PrinterStatus.OPERATIONAL;
    }

    if (IPAD_PRINTER_CONNECTION_TYPES.includes(this.connectionType)) {
      const devices = this.listableDevices;
      const connectedOnlineDevices = this.connectedOnlineDevices;
      const connectedDevices = this.connectedDevices;

      if (isBlank(devices)) {
        return PrinterStatus.NOT_IN_USE;
      } else if (isBlank(connectedDevices)) {
        return PrinterStatus.OFFLINE;
      } else if (devices.length !== connectedOnlineDevices.length) {
        return PrinterStatus.CHECK_IPADS;
      } else {
        return PrinterStatus.OPERATIONAL;
      }
    }

    return PRINTER_STATUS_MAP[this.status] ?? UNHANDLED_PRINTER_STATUS;
  }

  /**
   * Whether a print job directed at this printer can land somewhere reachable.
   *
   * Intentionally distinct from `printerStatus`:
   *   - `printerStatus` is pill-health: is THIS printer directly reachable?
   *     (primary-iPad `printerStatus` only — failover status on the same iPad
   *     refers to a *different* printer and cannot vouch for this one.)
   *   - `hasConnectedPath` is reachability: can an iPad paired with this
   *     printer land a job either directly OR by routing through its own
   *     failover? The feature-flag-gated print pipeline (`new-visitor-form`,
   *     `reprint-badge-modal`) uses this to decide whether to enable a
   *     printer option even if its own status is "Not connected".
   *
   * A past footgun (the "Fake Printer" regression covered in
   * tests/unit/models/printer-test.ts) conflated the two; keep them separate.
   */
  get hasConnectedPath(): boolean {
    if (ALWAYS_OPERATIONAL_CONNECTION_TYPES.includes(this.connectionType)) {
      return true;
    }
    if (!IPAD_PRINTER_CONNECTION_TYPES.includes(this.connectionType)) {
      // printnode and any other non-iPad-paired type: defer to the printer's own status.
      return PRINTER_STATUS_MAP[this.status] === PrinterStatus.OPERATIONAL;
    }
    const devices = this.listableDevices;
    if (isBlank(devices)) {
      return false;
    }
    const matching = devices.filter((device) => {
      if (!device.isIpadSeenRecently) return false;
      // Primary reads through to devices-service telemetry (SQ-7730). Failover stays on
      // the envoy-web attribute by design: telemetry carries no failover field, and the
      // iPad PATCH that writes it is not heartbeat-gated, so envoy-web stays live for it.
      const primaryOk = device.livePrinterStatus?.toLowerCase() === 'connected';
      const failoverOk = device.failoverPrinterStatus?.toLowerCase() === 'connected';
      return primaryOk || failoverOk;
    });
    return matching.length > 0;
  }

  get isOperational(): boolean {
    return this.printerStatus === PrinterStatus.OPERATIONAL;
  }

  get statusDisplay(): string {
    switch (this.printerStatus) {
      case PrinterStatus.OPERATIONAL:
        return 'Operational';
      case PrinterStatus.OFFLINE:
        return 'Offline';
      case PrinterStatus.NOT_IN_USE:
        return 'Not In Use';
      case PrinterStatus.CHECK_IPADS:
        return 'Check iPads';
      default:
        return 'Unknown';
    }
  }
}
